import React, {
  type ElementType,
  type FC,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState
} from 'react';
import { NavLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material';

import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { transforms, transitions } from '@/theme/constants';

interface DropdownMenuProps {
  items: {
    Icon?: ElementType;
    Component?: ElementType;
    text?: string;
    to?: string;
    isDivider?: boolean;
    onClick?: ((e: ReactMouseEvent<HTMLElement, MouseEvent>) => void) | (() => void);
  }[];
  avatar: ReactNode;
  title: string;
}

const ARROW_BUTTON_MARGIN = 0.75;
const DROPDOWN_BUTTON_PADDING = 1;
const MIN_ICON_WIDTH = 4;
const CARET_SIZE = 2;
const MAIN_AVATAR_SIZE = 28;

const DropdownAvatarMenu: FC<DropdownMenuProps> = ({ title, items, avatar }) => {
  const popperRef = useRef();
  const { spacing } = useTheme();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipOpen = useCallback(() => setTooltipOpen(true), []);

  const handleTooltipClose = useCallback(() => setTooltipOpen(false), []);

  const handleClose = useCallback(() => {
    (popperRef.current! as { close?: () => void })?.close?.();
    handleTooltipClose();
  }, [handleTooltipClose]);

  const handleClickAndClose = useCallback(
    (onClick: undefined | ((e: ReactMouseEvent<HTMLElement, MouseEvent>) => void)) =>
      (e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
        handleClose();
        onClick?.(e);
      },
    [handleClose]
  );

  const caretEndPosition = spacing(DROPDOWN_BUTTON_PADDING + ARROW_BUTTON_MARGIN + CARET_SIZE / 2);

  return (
    <>
      <StandardPopper
        getSx={() => ({ zIndex: 2000 })}
        placement="bottom-end"
        ref={popperRef}
        caretEndPosition={caretEndPosition}
        content={
          <Paper
            elevation={1}
            sx={{
              '.StandardPopper-root[data-popper-placement="bottom-end"] &': { mt: 0 }
            }}
          >
            <MenuList
              sx={{
                '& .MuiListItemIcon-root': {
                  minWidth: spacing(MIN_ICON_WIDTH)
                }
              }}
            >
              {items.map(({ Icon, Component, text, to, isDivider, onClick }, index) => {
                if (isDivider) {
                  return <Divider key={index} />;
                }

                return (
                  <MenuItem onClick={handleClickAndClose(onClick)} key={index}>
                    <Stack
                      direction="row"
                      to={to}
                      component={to ? NavLink : 'div'}
                      sx={{ textDecoration: 'none', color: 'text.primary' }}
                    >
                      {Icon && (
                        <ListItemIcon>
                          <Icon />
                        </ListItemIcon>
                      )}
                      {Component && <Component onClick={handleClose} onChange={handleClose} />}
                      {text}
                    </Stack>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        }
      >
        {({ isOpen }) => (
          <Tooltip title={title} open={!isOpen && tooltipOpen} onOpen={handleTooltipOpen} onClose={handleTooltipClose}>
            <IconButton
              sx={{
                borderRadius: 2,
                '& .MuiButtonBase-root': {
                  p: 0
                },
                '& .MuiButtonBase-root:hover': {
                  backgroundColor: 'transparent'
                },
                p: DROPDOWN_BUTTON_PADDING
              }}
            >
              <Avatar sx={{ width: MAIN_AVATAR_SIZE, height: MAIN_AVATAR_SIZE }}>{avatar}</Avatar>
              <Box
                sx={{
                  m: ARROW_BUTTON_MARGIN,
                  '& .MuiSvgIcon-root': {
                    width: spacing(CARET_SIZE),
                    height: spacing(CARET_SIZE),
                    transform: transforms.mirrorY(isOpen),
                    transition: transitions.transform
                  }
                }}
              >
                <ExpandMoreIcon />
              </Box>
            </IconButton>
          </Tooltip>
        )}
      </StandardPopper>
    </>
  );
};

export default DropdownAvatarMenu;
